// Mapping of backend warning codes to user-friendly labels & descriptions
// Keep list synchronized with backend shared semantics (see AI Metrics Semantics summary)

export interface AIWarningMeta {
  code: string;
  label: string;
  description: string;
  severity: "info" | "warn" | "error";
}

const WARNING_MAP: Record<string, AIWarningMeta> = {
  cable_gauge_mismatch: {
    code: "cable_gauge_mismatch",
    label: "Cable Gauge Mismatch",
    description:
      "Recommended deepest cable gauge differs from selected; verify ampacity and voltage drop.",
    severity: "warn"
  },
  motor_margin_percent_negative: {
    code: "motor_margin_percent_negative",
    label: "Motor Overload",
    description:
      "Calculated motor load exceeds rated HP (negative margin). Consider higher HP or reducing load.",
    severity: "error"
  },
  motor_hp_below_calculated_requirement: {
    code: "motor_hp_below_calculated_requirement",
    label: "Motor HP Below Requirement",
    description:
      "Selected motor nameplate HP is below the calculated load requirement. Select a higher-HP motor.",
    severity: "error"
  },
  motor_load_low: {
    code: "motor_load_low",
    label: "Low Motor Load",
    description:
      "Calculated motor load is below 70% of rated capacity. Consider a smaller motor or verify the calculated load.",
    severity: "warn"
  },
  motor_load_high: {
    code: "motor_load_high",
    label: "High Motor Load",
    description:
      "Calculated motor load is above 130% of rated capacity. Select a larger motor or reduce the load.",
    severity: "error"
  },
  bep_proximity_low: {
    code: "bep_proximity_low",
    label: "Far From BEP",
    description:
      "Operating point is far from pump best efficiency point; efficiency/runlife may degrade.",
    severity: "warn"
  },
  free_gas_fraction_null: {
    code: "free_gas_fraction_null",
    label: "Free Gas Unknown",
    description:
      "Free gas fraction could not be computed due to missing or invalid inputs.",
    severity: "info"
  },
  free_gas_high: {
    code: "free_gas_high",
    label: "High Free Gas",
    description:
      "Gas entering the pump exceeds 25%. Review intake pressure and fluid inputs, then consider additional gas separation.",
    severity: "warn"
  },
  pump_efficiency_low: {
    code: "pump_efficiency_low",
    label: "Low Pump Efficiency",
    description:
      "Calculated pump efficiency is below the minimum for this recommendation priority. Review pump fit and operating rate.",
    severity: "warn"
  },
  missing_operating_window: {
    code: "missing_operating_window",
    label: "Operating Window Missing",
    description:
      "Rate / frequency operating window limits unavailable; validate constraints manually.",
    severity: "info"
  },
  agentcore_recommendation_fallback: {
    code: "agentcore_recommendation_fallback",
    label: "Fallback Engine Used",
    description:
      "The calculation-verified catalog pipeline could not return a recommendation, so a local fallback estimate is shown. See specific reasons below.",
    severity: "warn"
  },
  baseline_component_reused: {
    code: "baseline_component_reused",
    label: "Baseline Component Reused",
    description:
      "No catalog candidate matched a required component, so the existing sizing component was retained. Verify that component manually.",
    severity: "info"
  },
  missing_deviation_survey: {
    code: "missing_deviation_survey",
    label: "Deviation Survey Missing",
    description:
      "No deviation survey was available. Review depth-dependent calculations and add the survey when available.",
    severity: "info"
  },
  missing_context_data: {
    code: "missing_context_data",
    label: "Scenario Data Missing",
    description:
      "Optional scenario context is missing. Review the sizing inputs because assumptions may reduce recommendation confidence.",
    severity: "info"
  },
  casing_clearance_unavailable: {
    code: "casing_clearance_unavailable",
    label: "Casing Clearance Unverified",
    description:
      "Component outside-diameter data was unavailable, so casing clearance could not be verified. Check equipment dimensions manually.",
    severity: "warn"
  },
  stale_equipment_metadata: {
    code: "stale_equipment_metadata",
    label: "Equipment Data Version Missing",
    description:
      "The sizing context did not include an equipment-data version. Refresh the sizing before relying on this recommendation.",
    severity: "info"
  },
  pump_bep_window_relaxed: {
    code: "pump_bep_window_relaxed",
    label: "Pump BEP Window Relaxed",
    description:
      "No Summit pump had a best-efficiency point within the target window; the nearest-BEP pump was selected. Fit may be suboptimal.",
    severity: "warn"
  },
  pump_flow_window_relaxed: {
    code: "pump_flow_window_relaxed",
    label: "Pump Flow Window Relaxed",
    description:
      "No Summit pump covered the target flow window; the nearest-BEP pump outside its rated window was selected. Verify operating range.",
    severity: "warn"
  },
  capacity_constraint_relaxed: {
    code: "capacity_constraint_relaxed",
    label: "Capacity Constraint Relaxed",
    description:
      "No Summit unit met the calculated capacity for a component; the largest available unit was selected. Verify sizing.",
    severity: "warn"
  },
  motor_voltage_relaxed: {
    code: "motor_voltage_relaxed",
    label: "Motor Voltage Relaxed",
    description:
      "No Summit motor at the existing nameplate voltage fit the horsepower window; a different-voltage motor was selected.",
    severity: "info"
  },
  high_strength_pump_material_required: {
    code: "high_strength_pump_material_required",
    label: "High-Strength Pump Material Required",
    description:
      "The standard pump burst rating is exceeded, but the high-strength rating passes. Use high-strength material for this design.",
    severity: "warn"
  }
};

const RANGE_WARNING_RE = /^no_summit_(.+)_in_range$/;

export function getWarningMeta(code: string): AIWarningMeta {
  const rangeMatch = code.match(RANGE_WARNING_RE);
  if (rangeMatch) {
    const equipment = rangeMatch[1].replace(/_/g, " ");
    return {
      code,
      label: `No Summit ${equipment} In Range`,
      description: `No Summit ${equipment} in the catalog fits this well's calculated requirement. A recommendation could not be generated for this configuration.`,
      severity: "error"
    };
  }
  return (
    WARNING_MAP[code] || {
      code,
      label: code.replace(/_/g, " "),
      description: "No description available for this warning.",
      severity: "info"
    }
  );
}

export function mapWarnings(
  codes: string[] | undefined | null
): AIWarningMeta[] {
  if (!codes || !codes.length) return [];
  return codes.map(getWarningMeta);
}
